// @flow

import { type Amount, type Fx, type Decimal, type CurrencyCode, type LocalTaxCode } from './types';

export type FormatDecimal = (Decimal) => Decimal;

type FormatAmount = (Amount) => string;
type FormatTaxPercentage = (?Decimal) => ?string;
type FormatFx = (Fx) => string;

export function amountToCurrencyString(formatDecimal: FormatDecimal): FormatAmount {
  return (amount: Amount) => {
    return `${getCurrencySymbol(amount[0])} ${formatDecimal(truncateDecimal(amount[1]))}`;
  };
}

export function taxRateToPercentageString(formatDecimal: FormatDecimal): FormatTaxPercentage {
  return (percentage: ?Decimal) => {
    if (percentage === null || percentage === undefined) {
      return null;
    }

    return `${formatDecimal(truncateDecimal(getRatePercentage(percentage)))}`;
  };
}

export function amountToValueString(formatDecimal: FormatDecimal): FormatAmount {
  return (amount: Amount) => {
    return `${formatDecimal(truncateDecimal(amount[1]))}`;
  };
}

export function fxToCurrencyString(formatDecimal: FormatDecimal): FormatFx {
  return (fx: Fx) => {
    return `${getCurrencySymbol(fx.to)} ${formatDecimal(truncateDecimal(fx.rate))}`;
  };
}

export function decimalToNumber(decimal: Decimal): number {
  const number = Number(decimal);

  if (isNaN(number) || parseFloat(decimal) !== number)
    throw new Error('invalid decimal');

  return number;
}

export function getRatePercentage(rate: Decimal): Decimal {
  return `${parseFloat(rate) * 100}`;
}

const currencySymbolMap: { [CurrencyCode]: string } = {
  BRL: 'R$',
  USD: 'US$',
  MXN: '$',
  ARS: '$',
  CLP: '$',
  EUR: '€',
  COP: 'COP',
  PEN: 'S/',
  BOB: 'Bs',
  UYU: '$U',
  CRC: '₡',
  PYG: '₲',
  PAB: 'B/.',
  GTQ: 'Q',
  DOP: 'RD$',
  ZAR: 'R',
  KES: 'KSh',
  NGN: '₦',
  INR: '₹',
  TZS: 'TZS',
  UGX: 'UGX',
  ZMW: 'ZMW',
  EGP: 'EGP',
  GHS: 'GHS',
  JMD: 'JM$',
  PHP: '₱',
  THB: '฿',
  IDR: 'Rp',
  TRY: '₺',
};

export function getCurrencySymbol(currencyCode: CurrencyCode): string {
  const symbol = currencySymbolMap[currencyCode];
  if (symbol == null) {
    throw new Error(`Currency symbol not supported: ${currencyCode}`);
  }
  return symbol;
}

export function truncateDecimal(value: Decimal): Decimal {
  return decimalToNumber(value).toFixed(2);
}

const localTaxCodeMap: { [string]: LocalTaxCode } = {
  iof: 'BR_IOF',
  br_iof: 'BR_IOF',
  iva: 'AR_IVA',
  ar_iva: 'AR_IVA',
  ar_ip: 'AR_IP',
  ig_ibp: 'AR_IG_IBP',
  ar_ig_ibp: 'AR_IG_IBP',
  ar_non_resident_igbp: 'AR_NON_RESIDENT_IGBP',
  ar_iibb: 'AR_IIBB_',
  py_iva: 'PY_IVA',
  cr_iva: 'CR_IVA',
  sv_itbms: 'SV_ITBMS',
  inr_gst: 'INR_GST',
  ph_vat: 'PH_VAT',
  th_vat: 'TH_VAT',
  id_vat: 'ID_VAT',
};

export function getLocalTaxCodeFromString(searchString: string): LocalTaxCode {
  const code = localTaxCodeMap[searchString.toLowerCase()];
  if (code == null) {
    throw new Error(`PRS/Money: ${searchString} is not related to any local tax code`);
  }
  return code;
}
